import { go } from '../../App';
import { useAllPages, useLoad } from '../../data';
import { InkHero, Loading, PageHead, Panel, Stars, formatWhen } from '../../ui';
import { Account, Booking } from '../../types';
import { BookingRecord } from './BookingRecord';

// Reviews (canon 09 supplier "Reviews" · Register; DECISION-056). The hotel
// rates the supplier after a completed job — 1-5 stars + an optional note — and
// this is where the supplier reads what they received. Two reads, both ones the
// supplier already makes: the aggregate hero from GET /account (the same record
// the Business profile shows), and the list from the SAME GET /v1/bookings the
// history reads (canon 19 U-S25 "see my history and my reviews"), filtered to
// the completed jobs that carry a review. Nothing here is a new mechanism — the
// review rides the booking, so a reviewed job IS the review. Read-only: the
// supplier receives reviews, they don't answer them (P9 stays guest-free).
export function Reviews({ openId }: { openId?: string }) {
  const account = useLoad<Account>('/account');
  const { items, error, loading } = useAllPages<Booking>('/bookings');

  const rating = account.data?.rating;
  const reviewed = items
    .filter((b) => b.review)
    .slice()
    .sort((a, b) => Date.parse(b.review!.at) - Date.parse(a.review!.at));

  return (
    <div>
      <PageHead title="Reviews" sub="What the hotels you work with say after the job is done." />

      <InkHero
        eyebrow="Your rating"
        figure={rating && rating.count > 0 ? rating.average.toFixed(1) : '—'}
        unit={rating && rating.count > 0 ? '/ 5' : undefined}
        explain={
          rating && rating.count > 0 ? (
            <>
              <Stars rating={rating.average} /> · from {rating.count} review{rating.count === 1 ? '' : 's'}
            </>
          ) : (
            'No reviews yet — a hotel can rate you once a job is completed.'
          )
        }
      />

      {error && <p className="error-line">{error}</p>}
      {loading && items.length === 0 ? (
        <Loading />
      ) : reviewed.length === 0 ? (
        <div className="empty-neutral">
          No reviews yet. When a hotel rates a completed job, it shows up here.
        </div>
      ) : (
        <Panel flush meta={`${reviewed.length} review${reviewed.length === 1 ? '' : 's'}`}>
          <ul className="review-list">
            {reviewed.map((b) => (
              <li key={b.id} className="review-row click" onClick={() => go(`/reviews/${b.id}`)}>
                <div className="review-head">
                  <Stars rating={b.review!.rating} />
                  <span className="hint">{formatWhen(b.review!.at)}</span>
                </div>
                {b.review!.note && <p className="review-note">“{b.review!.note}”</p>}
                <p className="hint review-job">
                  {b.hotelName ?? 'A hotel'} · {b.productName} · {formatWhen(b.start)}
                </p>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {openId && (
        <BookingRecord id={openId} backTo="/reviews" tz={account.data?.timezone ?? 'Europe/Athens'} />
      )}
    </div>
  );
}
